import type { AppRouterInputs } from '@alison/api/router';
import { skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from './trpc.js';

const NO_STREAM = { context: { stream: false } };
const RUNNING_POLL_MS = 10_000;
const LIST_POLL_MS = 60_000;

function useInvalidateAutomations() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: trpc.automations.pathKey() });
}

export function useAutomationDraft(sessionId: string | null) {
  const trpc = useTRPC();
  return useQuery(
    trpc.automations.draft.queryOptions(sessionId === null ? skipToken : { sessionId }, {
      refetchOnMount: 'always',
      trpc: NO_STREAM,
    }),
  );
}

export function useAutomationForSession(sessionId: string | null) {
  const trpc = useTRPC();
  return useQuery(
    trpc.automations.forSession.queryOptions(sessionId === null ? skipToken : { sessionId }, {
      trpc: NO_STREAM,
    }),
  );
}

export function useCreateAutomation() {
  const trpc = useTRPC();
  const invalidate = useInvalidateAutomations();
  return useMutation(
    trpc.automations.create.mutationOptions({ trpc: NO_STREAM, onSuccess: invalidate }),
  );
}

// Occurrences finish and slots roll forward on the server, so the list keeps polling.
export function useAutomations(filters: AppRouterInputs['automations']['list']) {
  const trpc = useTRPC();
  return useQuery(
    trpc.automations.list.queryOptions(filters, {
      refetchInterval: (query) =>
        query.state.data?.some((row) => row.lastRun?.status === 'running')
          ? RUNNING_POLL_MS
          : LIST_POLL_MS,
      trpc: NO_STREAM,
    }),
  );
}

export function useAutomation(id: string | null) {
  const trpc = useTRPC();
  return useQuery(
    trpc.automations.get.queryOptions(id === null ? skipToken : { id }, {
      refetchOnMount: 'always',
      trpc: NO_STREAM,
    }),
  );
}

export function useUpdateAutomation() {
  const trpc = useTRPC();
  const invalidate = useInvalidateAutomations();
  return useMutation(
    trpc.automations.update.mutationOptions({ trpc: NO_STREAM, onSuccess: invalidate }),
  );
}

export function useSetAutomationStatus() {
  const trpc = useTRPC();
  const invalidate = useInvalidateAutomations();
  return useMutation(
    trpc.automations.setStatus.mutationOptions({ trpc: NO_STREAM, onSettled: invalidate }),
  );
}

export function useDeleteAutomation() {
  const trpc = useTRPC();
  const invalidate = useInvalidateAutomations();
  return useMutation(
    trpc.automations.delete.mutationOptions({ trpc: NO_STREAM, onSuccess: invalidate }),
  );
}

export function useRunAutomationNow() {
  const trpc = useTRPC();
  const invalidate = useInvalidateAutomations();
  return useMutation(
    trpc.automations.runNow.mutationOptions({ trpc: NO_STREAM, onSuccess: invalidate }),
  );
}

// The label shows on runs, sessions and outcomes, so all of workflows refetches.
export function useRenameRun() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  return useMutation(
    trpc.automations.renameRun.mutationOptions({
      trpc: NO_STREAM,
      onSuccess: () => queryClient.invalidateQueries({ queryKey: trpc.workflows.pathKey() }),
    }),
  );
}

export function useDeliveryLog(id: string | null) {
  const trpc = useTRPC();
  return useQuery(
    trpc.automations.deliveryLog.queryOptions(id === null ? skipToken : { id }, {
      refetchOnMount: 'always',
      trpc: NO_STREAM,
    }),
  );
}
